import { useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { Col, Container, Form, FormControl, FormGroup, FormLabel, Row } from 'react-bootstrap';
import { usePublicOrganization } from '../hooks/usePublicOrganization';
import { useForm } from 'react-hook-form';
import { TextProvider, useText } from '../context/TextContext';
import AddressSearch from '../components/AddressSearch';
import { Place } from '../models/shared/Place';
import { AdvancedButton } from '../components/AdvancedButton';
import { validateEmail } from '../utils/validations';
import { DealPublicInput, DealPublicInputWithPlace, FileAttachment } from '../models/shared/DealPublicInput';
import { addAttachementsToDeal, createDealFromForm } from '../utils/api';
import { toast } from 'react-toastify';
import PublicNewDealFileAttachments from '../components/PublicNewDealFileAttachments';
import { Attachement } from '../models/shared/Attachment';
import { useUploadDealAttachment } from '../hooks/useUploadDealAttachement';
import LoadingPage from './LoadingPage';
import { MARKETING_WEBSITE } from '../utils/shared/constants';
import MapPublicForm from '../components/MapPublicForm';
import CustomerCard from '../components/CustomerCard';
import { Customer } from '../models/shared/Customer';
import { OrganizationPublic } from '../models/shared/Organization';
import { getBrowserLanguage } from '../utils/presentation';

export default function PublicNewDealPage() {
    const orgId = useParams<{ orgId: string }>().orgId;
    const organization = usePublicOrganization({ orgId: orgId || '' });

    if (!orgId || !organization) {
        return <LoadingPage />
    }

    return (
        <TextProvider language={organization.settings?.language || getBrowserLanguage()}>
            <PublicNewDealForm orgId={orgId} organization={organization} />
        </TextProvider>
    )
}

interface PublicNewDealFormProps {
    orgId: string;
    organization: OrganizationPublic;
}

function PublicNewDealForm({ orgId, organization }: PublicNewDealFormProps) {
    const text = useText();
    const { register, handleSubmit, formState: { errors }, watch } = useForm<DealPublicInput>();
    const [place, setPlace] = useState<Place | undefined>();
    const [processing, setProcessing] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [fileAttachements, setFileAttachements] = useState<FileAttachment[]>([]);
    const uploadDealAttachement = useUploadDealAttachment(orgId || '');

    const onSubmit = async (data: DealPublicInput) => {
        setProcessing(true);

        const formData: DealPublicInputWithPlace = {
            ...data,
            place,
            orgId,
        };

        try {
            const dealId = await createDealFromForm(formData);

            if (fileAttachements.length > 0) {
                const newAttachements: Attachement[] = []

                for (const fileAttachement of fileAttachements) {
                    const newAttachement = await uploadDealAttachement(dealId, fileAttachement.file);
                    if (newAttachement) {
                        newAttachements.push(newAttachement);
                    }
                }
                await addAttachementsToDeal(orgId, dealId, newAttachements);
            }


            setSubmitted(true);
        } catch (error) {
            console.error('Error creating deal:', error);
            toast.error(text.defaultError);
        }

        setProcessing(false);
    };

    const displayCustomer: Customer = {
        name: watch('customerName'),
        email: watch('customerEmail'),
        phone: watch('customerPhone'),
        id: '',
        idNumber: '',
        type: 'person',
        note: '',
        lastName: '',
        status: 'active',
    }

    const renderSubmitButton = () => <AdvancedButton
        variant="primary"
        type="submit"
        processing={processing}
        icon='bi-send'
    >
        {text.send}
    </AdvancedButton>

    return (
        <div>
            <Form onSubmit={handleSubmit(onSubmit)} className="position-relative">
                <div className='py-2 position-sticky top-0 bg-body z-1000'>

                    <Container className='d-flex justify-content-end'>
                        {renderSubmitButton()}
                    </Container>
                </div>


                <Container className="bg-offer p-4 rounded shadow-sm">
                    <div className="text-center">
                        <h4>{organization.settings?.customerFormIntroText || text.tellUseAboutYourProject}</h4>
                        <p className="text-muted mb-0">{organization.name}</p>
                    </div>
                    <hr className='primary my-4' />
                    <div>
                        {submitted ? (
                            <div className="text-center py-4">
                                <div className="bi bi-check-circle text-success" style={{ fontSize: '3rem' }}></div>
                                <h4 className="mt-3">{text.defaultSuccess}</h4>
                                <p className="text-muted">{organization.settings?.customerFormThankYouText || text.treeServiceThankYou}</p>
                            </div>
                        ) : (
                            <div className='d-flex flex-column gap-4 '>

                                <FormGroup>
                                    <FormLabel>{text.description}</FormLabel>
                                    <FormControl
                                        as="textarea"
                                        rows={4}
                                        {...register('description', { required: true })}
                                        isInvalid={!!errors.description}
                                    />
                                    {errors.description && <Form.Control.Feedback type="invalid">
                                        {text.thisFieldIsRequired}
                                    </Form.Control.Feedback>}
                                </FormGroup>

                                <FormGroup className='d-flex flex-column gap-3'>
                                    <div>

                                        <FormLabel>{text.address}</FormLabel>
                                        <AddressSearch
                                            place={place}
                                            onPlaceChange={async newPlace => setPlace(newPlace)}
                                        />
                                    </div>
                                    <MapPublicForm
                                        placeGeocoding={place?.geocoding}
                                        onPlaceChange={setPlace}
                                    />
                                </FormGroup>

                                <PublicNewDealFileAttachments
                                    attachments={fileAttachements}
                                    onAttachmentsChange={setFileAttachements}
                                />

                                <hr className='primary my-4' />

                                <div className="">
                                    <div className='center'>
                                        <h4>{text.customer}</h4>
                                    </div>

                                    <Row>
                                        <Col xs={12} md={6}>
                                            <CustomerCard customer={displayCustomer} noIdentifier={true} />
                                        </Col>

                                        <Col xs={12} md={6} className='gap-4 d-flex flex-column mt-4 mt-md-0'>
                                            <FormGroup className="">
                                                <FormLabel>{text.name}</FormLabel>
                                                <FormControl
                                                    {...register('customerName', { required: true })}
                                                    isInvalid={!!errors.customerName}
                                                />
                                                {errors.customerName && <Form.Control.Feedback type="invalid">
                                                    {text.thisFieldIsRequired}
                                                </Form.Control.Feedback>}
                                            </FormGroup>

                                            <FormGroup>
                                                <FormLabel>{text.email}</FormLabel>
                                                <FormControl
                                                    type="email"
                                                    {...register('customerEmail', {
                                                        required: true,
                                                        validate: validateEmail,
                                                    })}
                                                    isInvalid={!!errors.customerEmail}
                                                />
                                                {errors.customerEmail && <Form.Control.Feedback type="invalid">
                                                    {text.thisFieldIsRequired}
                                                </Form.Control.Feedback>}
                                            </FormGroup>
                                            <FormGroup>
                                                <FormLabel>{text.phone}</FormLabel>
                                                <FormControl
                                                    {...register('customerPhone', { required: true })}
                                                    isInvalid={!!errors.customerPhone}
                                                />
                                                {errors.customerPhone && <Form.Control.Feedback type="invalid">
                                                    {text.thisFieldIsRequired}
                                                </Form.Control.Feedback>}
                                            </FormGroup>
                                        </Col>

                                    </Row>

                                </div>
                                <div className='d-flex justify-content-end'>
                                    {renderSubmitButton()}
                                </div>
                            </div>
                        )}
                    </div>
                </Container>

                <Container className='center my-4'>
                    <Link to={MARKETING_WEBSITE}>
                        <p className='text-muted'>{text.poweredByArbTask}</p>
                    </Link>
                </Container>
            </Form>
        </div>
    )
}
